import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, ExternalLink, FileText, MoveUp, MoveDown, Copy, Eye, Zap, Sparkles } from 'lucide-react';
import { createForm, getForms, deleteForm, updateForm, aiGenerateForm } from '../lib/api';

const FIELD_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'email', label: 'Email' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'select', label: 'Dropdown Select' },
  { value: 'checkbox', label: 'Checkbox' },
];

export default function FormBuilder() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [ctaText, setCtaText] = useState('Submit');
  const [successMsg, setSuccessMsg] = useState('Thank you for your submission!');
  const [fields, setFields] = useState([]);
  const [aiTopic, setAiTopic] = useState('');
  const [aiPurpose, setAiPurpose] = useState('lead_capture');
  const [aiGenerating, setAiGenerating] = useState(false);

  useEffect(() => { loadForms(); }, []);

  const loadForms = async () => {
    try {
      const res = await getForms();
      setForms(res.data);
    } catch (err) {
      toast.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const addField = (type) => {
    const newField = {
      id: `field_${Date.now()}`,
      type,
      label: '',
      placeholder: '',
      required: false,
      options: type === 'select' ? ['Option 1', 'Option 2'] : [],
    };
    setFields([...fields, newField]);
  };

  const handleAIGenerate = async () => {
    if (!aiTopic.trim()) { toast.error('Enter a topic for AI generation'); return; }
    setAiGenerating(true);
    try {
      const res = await aiGenerateForm({ topic: aiTopic, form_purpose: aiPurpose, num_fields: 5 });
      if (res.data) {
        setFormName(res.data.name || '');
        setFormDesc(res.data.description || '');
        setCtaText(res.data.cta_text || 'Submit');
        setSuccessMsg(res.data.success_message || 'Thank you!');
        setFields(res.data.fields || []);
        toast.success('Form generated by AI!');
      }
    } catch (err) {
      toast.error('AI generation failed');
    } finally {
      setAiGenerating(false);
    }
  };

  const updateField = (index, key, value) => {
    const updated = [...fields];
    updated[index] = { ...updated[index], [key]: value };
    setFields(updated);
  };

  const removeField = (index) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const moveField = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= fields.length) return;
    const updated = [...fields];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setFields(updated);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error('Form name is required');
      return;
    }
    if (fields.length === 0) {
      toast.error('Add at least one field');
      return;
    }
    for (const f of fields) {
      if (!f.label.trim()) {
        toast.error('All fields must have a label');
        return;
      }
    }

    const data = {
      name: formName,
      description: formDesc,
      fields,
      cta_text: ctaText,
      success_message: successMsg,
    };

    try {
      if (editingForm) {
        await updateForm(editingForm.id, data);
        toast.success('Form updated');
      } else {
        await createForm(data);
        toast.success('Form created');
      }
      resetBuilder();
      loadForms();
    } catch (err) {
      toast.error('Failed to save form');
    }
  };

  const resetBuilder = () => {
    setShowBuilder(false);
    setEditingForm(null);
    setFormName('');
    setFormDesc('');
    setCtaText('Submit');
    setSuccessMsg('Thank you for your submission!');
    setFields([]);
  };

  const editForm = (form) => {
    setEditingForm(form);
    setFormName(form.name);
    setFormDesc(form.description || '');
    setCtaText(form.cta_text || 'Submit');
    setSuccessMsg(form.success_message || '');
    setFields(form.fields || []);
    setShowBuilder(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this form?')) return;
    try {
      await deleteForm(id);
      setForms(forms.filter(f => f.id !== id));
      toast.success('Form deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';

  if (showBuilder) {
    return (
      <div className="page-container">
        <div className="page-header flex items-center justify-between">
          <div>
            <h1 className="page-title">{editingForm ? 'Edit Form' : 'Create Form'}</h1>
            <p className="page-subtitle">Build a CTA form to capture leads</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={resetBuilder}>Cancel</Button>
            <Button onClick={handleSave} className="bg-[hsl(var(--accent))] text-white" data-testid="form-builder-save-button">
              {editingForm ? 'Update Form' : 'Create Form'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Builder */}
          <div className="space-y-4">
            {/* AI Generation */}
            <Card className="bg-card/90 border border-[hsl(var(--accent)/0.3)]" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2"><Sparkles className="w-4 h-4" style={{color: 'hsl(var(--accent))'}} /> AI Form Generator</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Topic / Context</Label>
                  <Input value={aiTopic} onChange={(e) => setAiTopic(e.target.value)}
                    placeholder="e.g., Viper Malware Assessment for Enterprise Security" className="bg-secondary/40" data-testid="ai-form-topic-input" />
                </div>
                <div className="flex gap-2">
                  <Select value={aiPurpose} onValueChange={setAiPurpose}>
                    <SelectTrigger className="bg-secondary/40 flex-1" data-testid="ai-form-purpose-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lead_capture">Lead Capture</SelectItem>
                      <SelectItem value="feedback">Feedback</SelectItem>
                      <SelectItem value="registration">Registration</SelectItem>
                      <SelectItem value="survey">Survey</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={handleAIGenerate} disabled={aiGenerating || !aiTopic.trim()}
                    className="bg-[hsl(var(--accent))] text-white" data-testid="ai-generate-form-btn">
                    <Sparkles className="w-4 h-4 mr-2" /> {aiGenerating ? 'Generating...' : 'Generate'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card/90 border border-border" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardContent className="p-5 space-y-4">
                <div>
                  <Label>Form Name</Label>
                  <Input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Contact Form" data-testid="form-name-input" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={formDesc} onChange={(e) => setFormDesc(e.target.value)} placeholder="Optional description" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>CTA Button Text</Label>
                    <Input value={ctaText} onChange={(e) => setCtaText(e.target.value)} placeholder="Submit" />
                  </div>
                  <div>
                    <Label>Success Message</Label>
                    <Input value={successMsg} onChange={(e) => setSuccessMsg(e.target.value)} placeholder="Thank you!" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Add Field */}
            <Card className="bg-card/90 border border-border" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Add Fields</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {FIELD_TYPES.map(ft => (
                  <Button key={ft.value} variant="outline" size="sm" onClick={() => addField(ft.value)}
                    data-testid={`form-builder-add-field-${ft.value}`}>
                    <Plus className="w-3.5 h-3.5 mr-1" /> {ft.label}
                  </Button>
                ))}
              </CardContent>
            </Card>

            {/* Field List */}
            {fields.map((field, idx) => (
              <Card key={field.id} className="bg-card/90 border border-border" style={{boxShadow: 'var(--shadow-elev-1)'}}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <Badge className="bg-secondary text-foreground text-xs">{FIELD_TYPES.find(f => f.value === field.type)?.label}</Badge>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => moveField(idx, -1)} disabled={idx === 0}>
                        <MoveUp className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => moveField(idx, 1)} disabled={idx === fields.length - 1}>
                        <MoveDown className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => removeField(idx)}>
                        <Trash2 className="w-3.5 h-3.5 text-[hsl(var(--status-danger))]" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs">Label</Label>
                      <Input value={field.label} onChange={(e) => updateField(idx, 'label', e.target.value)}
                        placeholder="Field label" className="bg-secondary/40" />
                    </div>
                    <div>
                      <Label className="text-xs">Placeholder</Label>
                      <Input value={field.placeholder} onChange={(e) => updateField(idx, 'placeholder', e.target.value)}
                        placeholder="Placeholder text" className="bg-secondary/40" />
                    </div>
                    {field.type === 'select' && (
                      <div>
                        <Label className="text-xs">Options (comma-separated)</Label>
                        <Input value={field.options?.join(', ')} onChange={(e) => updateField(idx, 'options', e.target.value.split(',').map(s => s.trim()))}
                          placeholder="Option 1, Option 2" className="bg-secondary/40" />
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Switch checked={field.required} onCheckedChange={(v) => updateField(idx, 'required', v)} />
                      <Label className="text-xs">Required</Label>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Preview */}
          <div>
            <Card className="bg-card/90 border border-border sticky top-6" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Form Preview</CardTitle>
              </CardHeader>
              <CardContent data-testid="form-preview">
                <div className="bg-white rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{formName || 'Form Name'}</h3>
                  <p className="text-sm text-gray-500 mb-4">{formDesc || 'Form description'}</p>
                  {fields.map((field) => (
                    <div key={field.id} className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label || 'Untitled Field'}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {field.type === 'textarea' ? (
                        <textarea className="w-full border border-gray-300 rounded-lg p-2 text-sm" placeholder={field.placeholder} rows={3} readOnly />
                      ) : field.type === 'select' ? (
                        <select className="w-full border border-gray-300 rounded-lg p-2 text-sm" readOnly>
                          <option>Select...</option>
                          {field.options?.map((opt, i) => <option key={i}>{opt}</option>)}
                        </select>
                      ) : field.type === 'checkbox' ? (
                        <div className="flex items-center gap-2">
                          <input type="checkbox" readOnly />
                          <span className="text-sm text-gray-700">{field.label}</span>
                        </div>
                      ) : (
                        <input type={field.type} className="w-full border border-gray-300 rounded-lg p-2 text-sm" placeholder={field.placeholder} readOnly />
                      )}
                    </div>
                  ))}
                  {fields.length === 0 && <p className="text-sm text-gray-400 text-center py-8">Add fields to see preview</p>}
                  {fields.length > 0 && (
                    <button className="w-full bg-cyan-600 text-white py-2.5 rounded-lg font-semibold text-sm mt-2">{ctaText}</button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Hero Header */}
      <div className="page-hero" data-testid="forms-hero">
        <div className="relative z-10 px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[hsl(var(--brand)/0.15)] border border-[hsl(var(--brand)/0.25)] flex items-center justify-center flex-shrink-0">
              <FileText className="w-6 h-6 text-[hsl(var(--brand))]" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--brand))] font-semibold mb-0.5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Lead Capture</p>
              <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Forms</h1>
              <p className="text-sm text-white/50 mt-0.5">Build CTA forms and capture leads</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-0.5">Forms</p>
              <p className="text-xl font-bold text-white font-mono">{forms.length}</p>
            </div>
            <Button onClick={() => setShowBuilder(true)} className="bg-[hsl(var(--accent-warm))] hover:bg-[hsl(var(--accent-warm)/0.88)] text-[hsl(var(--accent-warm-foreground))] btn-press" data-testid="form-builder-add-field-button">
              <Plus className="w-4 h-4 mr-2" /> New Form
            </Button>
          </div>
        </div>
      </div>

      {forms.length === 0 ? (
        <Card className="bg-[hsl(var(--pane))] border border-white/10 animate-fade-in-up" style={{boxShadow: 'var(--shadow-elev-1)'}}>
          <CardContent className="text-center py-16">
            <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
            <p className="text-muted-foreground mb-4">No forms yet</p>
            <Button onClick={() => setShowBuilder(true)} className="bg-[hsl(var(--accent-warm))] hover:bg-[hsl(var(--accent-warm)/0.88)] text-[hsl(var(--accent-warm-foreground))] btn-press">Create Your First Form</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-grid">
          {forms.map(form => (
            <Card key={form.id} className="bg-[hsl(var(--pane))] border border-white/10 card-hover-lift" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-medium">{form.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1">{form.description || 'No description'}</p>
                  </div>
                  <Badge className="bg-[hsl(var(--brand)/0.12)] text-[hsl(var(--brand))] border border-[hsl(var(--brand)/0.2)] text-xs">{form.fields?.length || 0} fields</Badge>
                </div>
                <p className="text-xs text-muted-foreground font-mono mb-3">
                  {form.submission_count || 0} submissions
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="btn-press" onClick={() => editForm(form)}>
                    <FileText className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <a href={`${baseUrl}/api/public/form/${form.id}`} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="btn-press">
                      <ExternalLink className="w-3.5 h-3.5 mr-1" /> Preview
                    </Button>
                  </a>
                  <Button variant="outline" size="sm" className="btn-press" onClick={() => {
                    navigator.clipboard.writeText(`${baseUrl}/api/public/form/${form.id}`);
                    toast.success('Form URL copied');
                  }}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" className="btn-press" onClick={() => handleDelete(form.id)}>
                    <Trash2 className="w-3.5 h-3.5 text-[hsl(var(--status-danger))]" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
